/**
 * whatIfApiService – Frontend API client for the what-if simulation engine.
 *
 * All calls go through the centralized apiClient (axios).
 * Never mutates database data — all simulation is server-side in-memory.
 */
import apiClient from '@/modules/shared/services/apiClient';

// ─── Types ──────────────────────────────────────────────

export interface WhatIfFieldChange {
  fieldKey: string;
  operation: 'PERCENT_INCREASE' | 'PERCENT_DECREASE' | 'SET_VALUE' | 'ADD_VALUE' | 'SUBTRACT_VALUE';
  value: number;
}

export interface WhatIfScopeFilter {
  fieldKey: string;
  operator: string;
  value: string;
}

export interface WhatIfSimulationRequest {
  scenarioName: string;
  entityType: string;
  changes: WhatIfFieldChange[];
  scope?: {
    reportId?: string;
    filters?: WhatIfScopeFilter[];
  };
}

export interface WhatIfKpiValue {
  kpiKey: string;
  label: string;
  numericValue: number;
  formatType: string;
  displayValue?: string;
}

export interface WhatIfKpiDelta {
  kpiKey: string;
  label: string;
  baselineValue: number;
  simulatedValue: number;
  absoluteChange: number;
  percentChange: number;
  direction: 'UP' | 'DOWN' | 'FLAT';
}

export interface WhatIfSimulationResult {
  scenarioName: string;
  baseline: { kpis: WhatIfKpiValue[] };
  simulated: { kpis: WhatIfKpiValue[] };
  delta: WhatIfKpiDelta[];
  affectedEntityCount: number;
}

// ─── Preset Scenarios ───────────────────────────────────

export interface WhatIfPreset {
  id: string;
  name: string;
  description: string;
  entityType: string;
  changes: WhatIfFieldChange[];
}

export const WHAT_IF_PRESETS: WhatIfPreset[] = [
  {
    id: 'increase-utilization',
    name: 'Increase utilization',
    description: 'Simulate higher drug utilization across all cases',
    entityType: 'CASE',
    changes: [{ fieldKey: 'utilized_amount', operation: 'PERCENT_INCREASE', value: 20 }],
  },
  {
    id: 'reduce-pending',
    name: 'Reduce pending approvals',
    description: 'Simulate faster approval turnaround',
    entityType: 'CASE',
    changes: [{ fieldKey: 'pending_days', operation: 'PERCENT_DECREASE', value: 50 }],
  },
  {
    id: 'improve-followup',
    name: 'Improve follow-up completion',
    description: 'Simulate higher follow-up call completion rate',
    entityType: 'PATIENT',
    changes: [{ fieldKey: 'followup_completion_rate', operation: 'PERCENT_INCREASE', value: 30 }],
  },
  {
    id: 'increase-approved',
    name: 'Increase approved amount',
    description: 'Simulate higher approved financial assistance',
    entityType: 'CASE',
    changes: [{ fieldKey: 'approved_amount', operation: 'PERCENT_INCREASE', value: 15 }],
  },
];

// ─── API Call ───────────────────────────────────────────

export async function runSimulation(
  programId: string,
  request: WhatIfSimulationRequest,
): Promise<WhatIfSimulationResult> {
  const { data } = await apiClient.post<{ data: WhatIfSimulationResult }>(
    `/programs/${programId}/what-if/simulate`,
    request,
  );
  return data.data;
}
